/**
 * Pure helpers backing the sharing UI's rendering decisions.
 *
 * Extracted from the components because each one encodes a rule that is easy to
 * get subtly wrong and impossible to see in a screenshot — which side of a
 * share the caller is on, and which grouping the "Shared" view is allowed to
 * build without a second request.
 */
import type { FlightShare, ShareCounterparties } from '../../types/api';
import { plural, type Translate } from '../../i18n/plural';
import type { TranslationKey } from '../../i18n/useTranslation';

/**
 * The part of a share these helpers actually read.
 *
 * Structural rather than `FlightShare`, because direction and liveness are
 * properties of the hub-and-spoke *mechanism*, not of what is being shared —
 * `ObjectShare` (#1182 Stage 4 slice 4) has the same two fields and the same
 * rules. Widening the parameter type is what lets the file/folder dialog reuse
 * these instead of growing a second copy that could drift on the one decision
 * that matters: who is allowed to see a Revoke button.
 */
export interface DirectionalShare {
  grantee_organization_id: string;
  revoked_at?: string | null;
  /**
   * The other organization on the share, named by the server.
   *
   * Optional because only the flight-share payload carries it today; the
   * object-share one does not, and an older backend sends neither. Every
   * helper here therefore treats it as a better answer when present, not as a
   * required one.
   */
  counterparty_organization_id?: string;
  counterparty_organization_name?: string | null;
}

/**
 * True when the caller's org is the one that *gave* this access away.
 *
 * Both share projections deliberately omit `granting_organization_id` (it is
 * always the shared thing's own org, so it carries no information the caller
 * does not already have), which leaves the grantee side as the only thing to
 * compare against. Direction is not cosmetic: only the granting org may revoke
 * — a grantee-side revoke attempt is answered 404, so offering the button on an
 * inbound share would be offering a control that cannot work.
 */
export function isOutboundShare(share: DirectionalShare, ownOrganizationId: string): boolean {
  return share.grantee_organization_id !== ownOrganizationId;
}

/** A share still granting access — i.e. not revoked. */
export function isLiveShare(share: DirectionalShare): boolean {
  return !share.revoked_at;
}

/**
 * Resolve the *other* organization's display name for a share.
 *
 * The server-derived name wins whenever it is there, and for a flight share it
 * always is. It is the only source that can name the GRANTOR of an inbound
 * share: the payload omits `granting_organization_id`, so before the server
 * started naming the counterparty the grantor could only be *inferred* — and
 * only for a caller with exactly one legal counterparty. That inference is
 * still the last resort for the object-share dialog, whose payload has no
 * counterparty name yet, and it is still bounded the same way: an XER caller
 * has many customers, so guessing would mislabel the row. `undefined` is the
 * honest answer there, and every caller turns it into a neutral label.
 */
export function counterpartyName(
  share: DirectionalShare,
  counterparties: ShareCounterparties | undefined
): string | undefined {
  if (share.counterparty_organization_name) return share.counterparty_organization_name;
  if (!counterparties) return undefined;

  if (isOutboundShare(share, counterparties.own_organization_id)) {
    return counterparties.organizations.find((o) => o.id === share.grantee_organization_id)?.name;
  }

  return counterparties.organizations.length === 1
    ? counterparties.organizations[0].name
    : undefined;
}

export interface DroneShareGroup {
  droneSerial: string | null;
  shares: FlightShare[];
}

/**
 * Group shares by the drone they belong to.
 *
 * Reads `share.drone_serial` — the share row's OWN denormalized column, written
 * by the hub-and-spoke trigger at insert time — never a value joined from the
 * flight. That is what lets a "Shared" listing exist without touching
 * `fleet.flights`, and specifically what keeps a *revoked* share identifiable:
 * once revoked, the grantee can no longer SELECT the flight, so anything
 * reached through the flight embed comes back null.
 *
 * Groups are ordered by serial so the list does not reshuffle between renders;
 * shares whose serial is null (only possible for rows written before the
 * trigger, i.e. none today) sort last under a null key rather than being
 * dropped, because silently hiding a grant is the worst possible failure for a
 * screen whose job is to show every grant.
 */
export function groupSharesByDrone(shares: FlightShare[]): DroneShareGroup[] {
  const groups = new Map<string, FlightShare[]>();
  // '' is used as the map key for a null serial — a real serial can never be
  // empty (the API constrains it to `^[A-Za-z0-9_-]{1,64}$`), so there is no
  // collision, and it keeps the map key type a plain string.
  for (const share of shares) {
    const key = share.drone_serial ?? '';
    const bucket = groups.get(key);
    if (bucket) bucket.push(share);
    else groups.set(key, [share]);
  }

  return [...groups.entries()]
    .sort(([a], [b]) => {
      if (a === b) return 0;
      if (a === '') return 1;
      if (b === '') return -1;
      return a.localeCompare(b, undefined, { numeric: true });
    })
    .map(([serial, groupShares]) => ({
      droneSerial: serial === '' ? null : serial,
      shares: groupShares,
    }));
}

// ---------------------------------------------------------------------------
// The outcome of a grant, as a sentence (#1498)
// ---------------------------------------------------------------------------

/** What a grant endpoint reports back — the same two numbers on both surfaces. */
export interface ShareOutcome {
  granted: number;
  skipped_existing: number;
}

/** The four dictionary keys a surface supplies: its own noun, both numbers. */
export interface ShareOutcomeKeys {
  sharedOne: TranslationKey;
  sharedOther: TranslationKey;
  skippedOne: TranslationKey;
  skippedOther: TranslationKey;
}

/**
 * "Shared 3 flights. 2 were already shared." — or just "Shared 1 flight."
 *
 * One composition for the flight dialog and the file dialog, because the two
 * used to carry separate `item(s)` / `flight(s)` strings that were wrong in the
 * same way. The skipped clause is omitted at zero rather than rendered as
 * "0 were already shared": nothing was skipped, so nothing is said about it.
 */
export function shareOutcomeMessage(
  t: Translate,
  outcome: ShareOutcome,
  keys: ShareOutcomeKeys
): string {
  const shared = plural(t, outcome.granted, { one: keys.sharedOne, other: keys.sharedOther });
  if (outcome.skipped_existing <= 0) return shared;
  const skipped = plural(t, outcome.skipped_existing, {
    one: keys.skippedOne,
    other: keys.skippedOther,
  });
  return `${shared} ${skipped}`;
}
